/*
  Keep the cart drawer for teeinblue add-to-cart.

  teeinblue ships with `atc_action: "redirect"`, and its bundle decides at click
  time:

      const Ik = e => I.getOptions().gotoCart || e.atc_action === "redirect"
      function Kf(){ window.location.href = `${Lo()}cart` }

  ...so after every personalized add-to-cart the shopper is thrown to /cart and
  the theme's drawer never opens. That also breaks the Frequently Bought
  Together step flow, which wants them to stay put.

  Because `e` is `window.teeinblueShopConfig` and it is read at click time, we
  can flip the flag in the page and open the theme drawer ourselves instead.

  NOTE: the durable fix is the teeinblue app setting itself ("add to cart
  action"). This is the theme-side equivalent so the behaviour is right until
  that is changed.

  2026-08-20: the app setting is now "Stay on the page", which uncovered a
  SECOND opener. The theme's own <product-form> (assets/product-form.js) also
  posts to /cart/add and then calls cart.renderContents(response), opening the
  drawer itself. While teeinblue was redirecting, that path never finished.
  Now both run, ~1s apart, and the drawer renders twice: it slides in, its
  innerHTML is swapped underneath it so it appears to vanish, then slides in
  again.

  We stand down rather than fight it, because the theme's own render is the
  better one - it already holds the cart response and needs no extra network
  call. The two are told apart by the request body: product-form appends
  `sections` and `sections_url` to its FormData, teeinblue's own add does not.
  So a /cart/add carrying `sections` is left entirely to the theme.

  A render-timestamp guard backs that up for the XHR path, where the body is
  not always inspectable.

  2026-08-21: timed the whole path and found the drawer cannot be made much
  faster - most of the wait is teeinblue's. What was missing was any feedback
  while it worked. Section 3 covers it; the numbers are recorded there.
*/
(function () {
  'use strict';

  /* ---------- 1. stop the redirect ---------- */

  function disarm() {
    var c = window.teeinblueShopConfig;
    if (c && c.atc_action === 'redirect') {
      c.atc_action = 'stay';
      return true;
    }
    return false;
  }

  disarm();
  // The config object is written by an async app script, so keep trying briefly.
  var tries = 0;
  var poll = setInterval(function () {
    if (disarm() || ++tries > 60) clearInterval(poll);
  }, 250);
  document.addEventListener('DOMContentLoaded', disarm);

  /* ---------- 2. open the drawer ourselves ---------- */

  var opening = false;

  /* When did the drawer last render, from ANY source - us or the theme?
     Anything inside this window counts as the same add and is not repeated. */
  var lastRender = 0;
  var RENDER_DEDUPE_MS = 2500;

  function watchRenders() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || drawer.__lhRenderWatched) return;
    if (typeof drawer.renderContents !== 'function') return;
    var native = drawer.renderContents;
    drawer.renderContents = function () {
      lastRender = Date.now();
      return native.apply(this, arguments);
    };
    drawer.__lhRenderWatched = true;
  }

  watchRenders();
  document.addEventListener('DOMContentLoaded', watchRenders);
  var wrapTries = 0;
  var wrapPoll = setInterval(function () {
    watchRenders();
    if (++wrapTries > 40) clearInterval(wrapPoll);
  }, 250);

  /* True when this /cart/add came from the theme's own <product-form>, which
     will render the drawer itself once its request resolves. */
  function themeWillRender(init) {
    try {
      var body = init && init.body;
      return !!(body && typeof body.get === 'function' && body.get('sections'));
    } catch (e) {
      return false;
    }
  }

  /* Pulse the header cart badge on a successful add. Reuses this file's
     existing fetch/XHR hooks rather than wrapping fetch a second time. */
  function lhCartPulse() {
    try {
      var b = document.querySelector('.cart-count');
      if (!b) return;
      b.classList.remove('lh-cart-ping');
      void b.offsetWidth;                 /* restart the animation */
      b.classList.add('lh-cart-ping');
      setTimeout(function () { b.classList.remove('lh-cart-ping'); }, 2800);
    } catch (e) {}
  }

  function openDrawer() {
    if (opening) return;
    watchRenders();
    if (Date.now() - lastRender < RENDER_DEDUPE_MS) return;
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.renderContents !== 'function') return;
    opening = true;

    var ids;
    try {
      ids = drawer
        .getSectionsToRender()
        .map(function (s) {
          return s.id;
        })
        .join(',');
    } catch (e) {
      ids = 'cart-drawer';
    }

    fetch('/?sections=' + ids)
      .then(function (r) {
        return r.json();
      })
      .then(function (sections) {
        drawer.renderContents({ sections: sections });
      })
      .catch(function () {
        drawer.open();
      })
      .then(function () {
        setTimeout(function () {
          opening = false;
        }, 800);
      });
  }

  function isCartAdd(url) {
    return typeof url === 'string' && url.indexOf('/cart/add') !== -1;
  }

  /* teeinblue posts the personalized line item itself — catch it either way. */

  var nativeFetch = window.fetch;
  if (nativeFetch) {
    window.fetch = function (input, init) {
      var url = input && input.url ? input.url : input;
      var promise = nativeFetch.apply(this, arguments);
      /* The badge pulse must fire on EVERY successful add. The drawer opener
         must NOT — the theme renders the drawer itself whenever it sends
         `sections`, and firing ours too gives a double drawer. Those are two
         different conditions, so they cannot share one guard: nesting the
         pulse inside !themeWillRender meant a normal product-page add (which
         always sends `sections`) skipped the pulse entirely, and it only ever
         ran on teeinblue adds. */
      if (isCartAdd(url)) {
        promise
          .then(function (res) {
            if (!res || !res.ok) return;
            lhCartPulse();
            if (!themeWillRender(init)) setTimeout(openDrawer, 250);
          })
          .catch(function () {});
      }
      return promise;
    };
  }

  var nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (isCartAdd(url)) {
      this.addEventListener('load', function () {
        if (this.status >= 200 && this.status < 300) (lhCartPulse(), setTimeout(openDrawer, 250));
      });
    }
    return nativeOpen.apply(this, arguments);
  };

  /* ---------- 3. acknowledge the click immediately ---------- */

  /* Measured in the browser 2026-08-21, three runs, consistent shape:

         CLICK                     0ms
         /cart/add START      +350..660ms   teeinblue pre-processing
         /cart/add DONE      +1020..1430ms  the request itself
         teeinblue customization POST       (attaches the preview image)
         our sections fetch  +1270..1600ms  <- the 250ms wait above
         DRAWER VISIBLE      +1490..1970ms

     ~70% of that is teeinblue's and not ours to shorten. The 250ms wait above
     IS load bearing: their customization POST lands ~50ms before we fire, so
     trimming it races the preview-image attach and renders a drawer with a
     missing thumbnail. Do not "optimise" it away.

     The real defect was that nothing acknowledged the click. Sampled every
     100ms across the whole 1.8s, the button never changed: same label, still
     enabled, opacity 1. Dead air, which is what reads as "the drawer takes
     three seconds".

     teeinblue already tracks this state - Vue puts `tee-processing` on the
     button at +14ms and takes it off at +1904ms, almost exactly the window we
     want. They just never wrote any CSS for it: adding the class produces zero
     computed-style change. So we only need to style it. No JS, no click
     listener, no timers, and nothing for Vue to clobber - an earlier attempt
     added our own class and Vue overwrote the whole attribute 14ms later.

     The label lives in a single child <span>, so hiding the child rather than
     setting `color: transparent` keeps `currentColor` intact and lets the
     spinner take the button's own text colour on any button style. */

  (function busyStyle() {
    if (document.getElementById('lh-atc-busy-style')) return;
    var st = document.createElement('style');
    st.id = 'lh-atc-busy-style';
    st.textContent =
      '.tee-btn.tee-processing{position:relative;cursor:wait;pointer-events:none}' +
      '.tee-btn.tee-processing>*{visibility:hidden}' +
      '.tee-btn.tee-processing::after{content:"";position:absolute;top:50%;left:50%;' +
      'width:16px;height:16px;margin:-8px 0 0 -8px;box-sizing:border-box;' +
      'border:2px solid currentColor;border-right-color:transparent;' +
      'border-radius:50%;animation:lh-atc-spin .6s linear infinite}' +
      '@keyframes lh-atc-spin{to{transform:rotate(360deg)}}' +
      '@media (prefers-reduced-motion:reduce){' +
      '.tee-btn.tee-processing::after{animation-duration:1.8s}}';
    (document.head || document.documentElement).appendChild(st);
  })();

})();

/* ---------------------------------------------------------------------------
   Yield the corner buttons to teeinblue's action block (mobile only)
   2026-09-01.

   The complaint was "the chat button is sitting on top of the personalize
   button". No amount of repositioning fixes that: teeinblue's PERSONALIZE
   header and its ADD TO CART / PREVIEW block are full-width and IN FLOW
   (measured at 375-500px: left 15 -> right 470), while the chat tab and the
   TOP tile are position:fixed in the bottom-right corner. Every full-width
   control therefore passes underneath them at some scroll position.

   So instead of moving anything, both corner buttons fade out while that block
   is on screen and come back once it scrolls away. Desktop is untouched -
   there is room there and the tab is a deliberate fixture.

   Re-scanned on mutation because teeinblue injects its form after page load,
   so the targets do not exist at DOMContentLoaded.
   --------------------------------------------------------------------------- */
(function () {
  var SEL = '.tee-form-actions, .tee-btn--atc, .tee-form-header, .product-form__buttons';
  var mq = window.matchMedia('(max-width: 749px)');
  var ticking = false;

  /* Deliberately NOT IntersectionObserver. IO only reports in a rendered,
     visible tab, which made it impossible to verify here and would fail the
     same way in any background/prerender context. A rAF-throttled rect test
     runs anywhere and is cheap: at most a handful of getBoundingClientRect
     calls per frame, and only while the viewport is under 750px. */
  function anyOnScreen() {
    var els = document.querySelectorAll(SEL);
    var vh = window.innerHeight;
    for (var i = 0; i < els.length; i++) {
      var r = els[i].getBoundingClientRect();
      if (!r.width || !r.height) continue;   /* collapsed nodes never count */
      if (r.top < vh && r.bottom > 0) return true;
    }
    return false;
  }

  function paint() {
    ticking = false;
    var on = mq.matches && anyOnScreen();
    document.documentElement.classList.toggle('lh-corner-yield', on);
  }

  function schedule() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(paint);
  }

  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule);
  window.addEventListener('load', schedule);
  if (mq.addEventListener) mq.addEventListener('change', schedule);
  else if (mq.addListener) mq.addListener(schedule);

  /* teeinblue injects its form well after DOMContentLoaded, so the first
     honest measurement is only possible once that lands. */
  var pending = null;
  new MutationObserver(function () {
    clearTimeout(pending);
    pending = setTimeout(schedule, 200);
  }).observe(document.documentElement, { childList: true, subtree: true });

  schedule();
  window.lhCornerYieldCheck = paint;   /* so this can be tested directly */
})();
